import { c as _c } from "react/compiler-runtime";
import { NodeIcon } from "@/components/ui/node-icon";
import { getExecutionStatusClasses } from "@/hooks/useNodeExecutionStatus";
import { cn } from "@/lib/utils";
import type { NodeExecutionStatus } from "@/types/runStream";
import { Handle, Position } from "@xyflow/react";
import React, { type ReactNode } from "react";
import { ExecutionStatusBadge } from "./ActionNodeStatus";

/**
 * Visual variants that the shell can display.
 *
 * - `idle` — default emboss + border
 * - `running` / `auto_swapping` / `success` / `failure` / `skipped` — pressed-in (deboss) +
 *   matching status background tint, paired with the {@link ExecutionStatusBadge} bubble.
 * - `waiting` — same emboss as idle but with the scheduled banner slot filled
 *   (consumer renders banner via `bannerSlot`); see also `isScheduled`.
 */
export type ActionNodeShellStatus = "idle" | NodeExecutionStatus;
export interface ActionNodeShellProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onMouseEnter" | "onMouseLeave"> {
  /** React Flow node id — only used as data attribute for live cursor targeting and a11y. */
  id?: string;
  /** Display name shown in bold next to the icon. Falls back to `subtitle` text. */
  name: string;
  /** Small line beneath the name (e.g. "Action", "Action output · 1.2s", "Connector required"). */
  subtitle?: ReactNode;
  /** Logo/icon URL for the node — passed through to {@link NodeIcon}. */
  iconUrl?: string;
  /** Optional connector overlay shown on top-right of the icon. */
  connectorIconUrl?: string;
  /** Connector type label (used by {@link NodeIcon} when no `connectorIconUrl` is set). */
  connectorType?: string;
  /** Execution status — drives badge, deboss, and tinted background. */
  status?: ActionNodeShellStatus;
  /** Whether the node is currently selected (drives blue border + shimmer ring). */
  selected?: boolean;
  /** Whether to show the connector-missing red dashed treatment. */
  needsConnector?: boolean;
  /** Whether to show the gas / token warning amber dashed treatment. */
  hasBalanceWarning?: boolean;
  /** Whether to show the validation error red dashed treatment. */
  hasValidationErrors?: boolean;
  /** Streaming-active highlight (AI is currently building this node). */
  isStreamingActive?: boolean;
  /** AI-update highlight (the node was just updated by AI). */
  showAIHighlight?: boolean;
  /** Shimmer ring shown for selection/hover when no other border treatment claims priority. */
  showRotatingBorder?: boolean;
  /** Same shimmer ring used on hover (without selection). */
  showHoverBorder?: boolean;
  /**
   * Streaming entrance / edit highlight class applied to the outer wrapper.
   * Kept as an opaque class string so the shell does not need to know the source of truth.
   */
  streamingClass?: string;
  /** Hide the source handle — useful for terminal nodes / template view. */
  hideSourceHandle?: boolean;
  /** Hide the target handle — useful for trigger-position usage of the shell. */
  hideTargetHandle?: boolean;
  /** When true, lays the handles horizontally (left target, right source) instead of vertically. */
  horizontalHandles?: boolean;
  /** Compact mode hides the subtitle row, banners, and body — used when the canvas is zoomed out. */
  compact?: boolean;
  /** Mouse handlers — owned by the wrapper because they may also update global hover state. */
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Optional ref forwarded to the outer wrapper (used by the wrapper to anchor toolbars). */
  nodeRef?: React.Ref<HTMLDivElement>;
  /** Toolbar slot — description / validation / gas warning toolbars rendered above the chrome. */
  toolbarSlot?: ReactNode;
  /** Banner slot — Scheduled / Polymarket / etc, rendered between header and body. */
  bannerSlot?: ReactNode;
  /** Body slot — settings preview, output preview, custom node content. */
  bodySlot?: ReactNode;
  /** Execution details slot — input/output details panel after a finished run. */
  executionDetailsSlot?: ReactNode;
  /** Comments badge slot — small overlay shown when the node has comments. */
  commentsBadgeSlot?: ReactNode;
  /** Selection indicator slot — collaborative editing presence indicator. */
  selectionIndicatorSlot?: ReactNode;
  /** Optional className for the outer wrapper (used for `data-api-node-id`-driven selectors). */
  className?: string;
  /** Optional data attribute used by selectors that target the API node id. */
  dataApiNodeId?: string;
  /** Color to pass to {@link NodeIcon} (defaults to action-green). */
  iconColor?: string;
  /** Optional shape: render in a "trigger" color theme for the icon (defaults to action). */
  iconKind?: "action" | "trigger";
  /** NodeIcon caching id (typically the action `type`). Falls back to {@link id} or `name`. */
  iconId?: string;
  /** NodeIcon subject (display name used for AI-generated icon prompts). Falls back to {@link name}. */
  iconSubject?: string;
  /** NodeIcon context (description used for AI-generated icon prompts). */
  iconContext?: string;
  /** NodeIcon alt text. Falls back to {@link name}. */
  iconAlt?: string;
}

/**
 * Pure presentational chrome for ActionNode.
 *
 * Owns the outer card surface, status colouring, handles, and slots for body,
 * banners, toolbars, comments, and the selection indicator. Calls **no hooks**
 * besides whatever `<Handle>` does internally — that is React Flow's
 * `useNodeId` / `useStore` and is provider-only (no fetch / subscription).
 *
 * Stories / Ladle wrap usages in `<ReactFlowProvider>` so the handles render.
 */
export function ActionNodeShell(t0) {
  const $ = _c(124);
  let bannerSlot;
  let bodySlot;
  let className;
  let commentsBadgeSlot;
  let connectorIconUrl;
  let connectorType;
  let dataApiNodeId;
  let executionDetailsSlot;
  let iconAlt;
  let iconColor;
  let iconContext;
  let iconId;
  let iconSubject;
  let iconUrl;
  let id;
  let name;
  let nodeRef;
  let onMouseEnter;
  let onMouseLeave;
  let rest;
  let selectionIndicatorSlot;
  let streamingClass;
  let subtitle;
  let t1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let toolbarSlot;
  if ($[0] !== t0) {
    ({
      id,
      name,
      subtitle,
      iconUrl,
      connectorIconUrl,
      connectorType,
      status: t1,
      selected: t2,
      needsConnector: t3,
      hasBalanceWarning: t4,
      hasValidationErrors: t5,
      isStreamingActive: t6,
      showAIHighlight: t7,
      showRotatingBorder: t8,
      showHoverBorder: t9,
      streamingClass,
      hideSourceHandle: t10,
      hideTargetHandle: t11,
      horizontalHandles: t12,
      compact: t13,
      onMouseEnter,
      onMouseLeave,
      nodeRef,
      toolbarSlot,
      bannerSlot,
      bodySlot,
      executionDetailsSlot,
      commentsBadgeSlot,
      selectionIndicatorSlot,
      className,
      dataApiNodeId,
      iconColor,
      iconKind: t14,
      iconId,
      iconSubject,
      iconContext,
      iconAlt,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = bannerSlot;
    $[2] = bodySlot;
    $[3] = className;
    $[4] = commentsBadgeSlot;
    $[5] = connectorIconUrl;
    $[6] = connectorType;
    $[7] = dataApiNodeId;
    $[8] = executionDetailsSlot;
    $[9] = iconAlt;
    $[10] = iconColor;
    $[11] = iconContext;
    $[12] = iconId;
    $[13] = iconSubject;
    $[14] = iconUrl;
    $[15] = id;
    $[16] = name;
    $[17] = nodeRef;
    $[18] = onMouseEnter;
    $[19] = onMouseLeave;
    $[20] = rest;
    $[21] = selectionIndicatorSlot;
    $[22] = streamingClass;
    $[23] = subtitle;
    $[24] = t1;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
    $[28] = t13;
    $[29] = t14;
    $[30] = t2;
    $[31] = t3;
    $[32] = t4;
    $[33] = t5;
    $[34] = t6;
    $[35] = t7;
    $[36] = t8;
    $[37] = t9;
    $[38] = toolbarSlot;
  } else {
    bannerSlot = $[1];
    bodySlot = $[2];
    className = $[3];
    commentsBadgeSlot = $[4];
    connectorIconUrl = $[5];
    connectorType = $[6];
    dataApiNodeId = $[7];
    executionDetailsSlot = $[8];
    iconAlt = $[9];
    iconColor = $[10];
    iconContext = $[11];
    iconId = $[12];
    iconSubject = $[13];
    iconUrl = $[14];
    id = $[15];
    name = $[16];
    nodeRef = $[17];
    onMouseEnter = $[18];
    onMouseLeave = $[19];
    rest = $[20];
    selectionIndicatorSlot = $[21];
    streamingClass = $[22];
    subtitle = $[23];
    t1 = $[24];
    t10 = $[25];
    t11 = $[26];
    t12 = $[27];
    t13 = $[28];
    t14 = $[29];
    t2 = $[30];
    t3 = $[31];
    t4 = $[32];
    t5 = $[33];
    t6 = $[34];
    t7 = $[35];
    t8 = $[36];
    t9 = $[37];
    toolbarSlot = $[38];
  }
  const status = t1 === undefined ? "idle" : t1;
  const selected = t2 === undefined ? false : t2;
  const needsConnector = t3 === undefined ? false : t3;
  const hasBalanceWarning = t4 === undefined ? false : t4;
  const hasValidationErrors = t5 === undefined ? false : t5;
  const isStreamingActive = t6 === undefined ? false : t6;
  const showAIHighlight = t7 === undefined ? false : t7;
  const showRotatingBorder = t8 === undefined ? false : t8;
  const showHoverBorder = t9 === undefined ? false : t9;
  const hideSourceHandle = t10 === undefined ? false : t10;
  const hideTargetHandle = t11 === undefined ? false : t11;
  const horizontalHandles = t12 === undefined ? false : t12;
  const compact = t13 === undefined ? false : t13;
  const iconKind = t14 === undefined ? "action" : t14;
  const hasActiveRun = status !== "idle";
  const isFailure = status === "failure";
  const isSkipped = status === "skipped";
  let connector;
  let resolvedIconColor;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  if ($[39] !== className || $[40] !== connectorIconUrl || $[41] !== connectorType || $[42] !== dataApiNodeId || $[43] !== hasActiveRun || $[44] !== hasBalanceWarning || $[45] !== hasValidationErrors || $[46] !== iconColor || $[47] !== iconKind || $[48] !== id || $[49] !== isFailure || $[50] !== isSkipped || $[51] !== isStreamingActive || $[52] !== needsConnector || $[53] !== nodeRef || $[54] !== onMouseEnter || $[55] !== onMouseLeave || $[56] !== rest || $[57] !== selected || $[58] !== showAIHighlight || $[59] !== status || $[60] !== streamingClass) {
    const statusClasses = hasActiveRun ? getExecutionStatusClasses(status as NodeExecutionStatus) : "";
    let t21;
    if ($[69] !== connectorIconUrl || $[70] !== connectorType) {
      t21 = connectorIconUrl ? {
        type: connectorType ?? "connector",
        logoUrl: connectorIconUrl
      } : connectorType ? {
        type: connectorType
      } : undefined;
      $[69] = connectorIconUrl;
      $[70] = connectorType;
      $[71] = t21;
    } else {
      t21 = $[71];
    }
    connector = t21;
    resolvedIconColor = iconColor ?? (iconKind === "trigger" ? "#2450FF" : "#22c55e");
    t15 = rest;
    t16 = nodeRef;
    t17 = dataApiNodeId ?? id;
    t18 = onMouseEnter;
    t19 = onMouseLeave;
    t20 = cn("border-ds-neutral-200 bg-ds-surface-secondary rounded-ds-14 group relative w-[280px] cursor-pointer overflow-visible border p-1.5 transition-[border-color,box-shadow,opacity]", streamingClass, hasActiveRun ? "node-deboss" : "node-emboss", showAIHighlight && "animate-ai-highlight bg-ds-surface-card shadow-ds-elev-2 border-dashed border-blue-500 shadow-blue-500/30 ring-2 ring-blue-500/50", !showAIHighlight && hasActiveRun && statusClasses, !showAIHighlight && needsConnector && !hasActiveRun && "border-dashed border-red-300 bg-red-400/10 backdrop-blur-sm hover:border-red-400 hover:bg-red-400/20 hover:shadow-md dark:border-red-400 dark:bg-red-950/20 dark:hover:border-red-300 dark:hover:bg-red-950/30", !showAIHighlight && hasBalanceWarning && !needsConnector && !hasActiveRun && !hasValidationErrors && "border-dashed border-amber-300 bg-amber-400/10 backdrop-blur-sm hover:border-amber-400 hover:bg-amber-400/20 hover:shadow-md dark:border-amber-400 dark:bg-amber-950/20 dark:hover:border-amber-300 dark:hover:bg-amber-950/30", !showAIHighlight && !hasActiveRun && !needsConnector && hasValidationErrors && "border-dashed border-red-500 bg-red-50/50 shadow-red-500/10 dark:bg-red-950/20", isStreamingActive && "streaming-active-node bg-ds-surface-card shadow-ds-elev-2 border-2 border-blue-500 shadow-blue-500/20 dark:border-blue-400 dark:shadow-blue-400/20", !isStreamingActive && !showAIHighlight && !hasActiveRun && !needsConnector && !hasBalanceWarning && !hasValidationErrors && (selected ? "node-selected-blue bg-ds-surface-card" : "border-ds-neutral-300 dark:border-ds-neutral-700 bg-ds-surface-card"), !showAIHighlight && isFailure && "bg-red-50 !backdrop-blur-sm dark:bg-red-950/80", !showAIHighlight && isSkipped && "bg-ds-neutral-100 dark:bg-ds-neutral-800 !backdrop-blur-sm", className);
    $[39] = className;
    $[40] = connectorIconUrl;
    $[41] = connectorType;
    $[42] = dataApiNodeId;
    $[43] = hasActiveRun;
    $[44] = hasBalanceWarning;
    $[45] = hasValidationErrors;
    $[46] = iconColor;
    $[47] = iconKind;
    $[48] = id;
    $[49] = isFailure;
    $[50] = isSkipped;
    $[51] = isStreamingActive;
    $[52] = needsConnector;
    $[53] = nodeRef;
    $[54] = onMouseEnter;
    $[55] = onMouseLeave;
    $[56] = rest;
    $[57] = selected;
    $[58] = showAIHighlight;
    $[59] = status;
    $[60] = streamingClass;
    $[61] = connector;
    $[62] = resolvedIconColor;
    $[63] = t15;
    $[64] = t16;
    $[65] = t17;
    $[66] = t18;
    $[67] = t19;
    $[68] = t20;
  } else {
    connector = $[61];
    resolvedIconColor = $[62];
    t15 = $[63];
    t16 = $[64];
    t17 = $[65];
    t18 = $[66];
    t19 = $[67];
    t20 = $[68];
  }
  let t21;
  if ($[72] !== showHoverBorder || $[73] !== showRotatingBorder) {
    t21 = (showRotatingBorder || showHoverBorder) && <><div className="shimmer-border-blue rounded-ds-lg pointer-events-none absolute -inset-[3px] -z-10 overflow-hidden" /><div className="rounded-ds-14 bg-ds-surface-card pointer-events-none absolute -inset-[1px] -z-10" /></>;
    $[72] = showHoverBorder;
    $[73] = showRotatingBorder;
    $[74] = t21;
  } else {
    t21 = $[74];
  }
  let t22;
  if ($[75] !== hideTargetHandle || $[76] !== horizontalHandles) {
    t22 = !hideTargetHandle && <Handle type="target" position={horizontalHandles ? Position.Left : Position.Top} className="!border-ds-neutral-300 !bg-ds-surface-card !h-3 !w-3 !border-2 dark:!border-zinc-600" />;
    $[75] = hideTargetHandle;
    $[76] = horizontalHandles;
    $[77] = t22;
  } else {
    t22 = $[77];
  }
  let t23;
  if ($[78] !== hasActiveRun || $[79] !== status) {
    t23 = hasActiveRun && <ExecutionStatusBadge executionStatus={status as NodeExecutionStatus} />;
    $[78] = hasActiveRun;
    $[79] = status;
    $[80] = t23;
  } else {
    t23 = $[80];
  }
  const t24 = iconId || id || name || "action";
  const t25 = iconSubject || name;
  const t26 = iconContext || "";
  const t27 = compact ? "h-8 w-8" : "h-10 w-10";
  const t28 = iconAlt || name;
  let t29;
  if ($[81] !== connector || $[82] !== iconUrl || $[83] !== resolvedIconColor || $[84] !== t24 || $[85] !== t25 || $[86] !== t26 || $[87] !== t27 || $[88] !== t28) {
    t29 = <NodeIcon id={t24} subject={t25} context={t26} logoUrl={iconUrl} connector={connector} className={t27} alt={t28} color={resolvedIconColor} />;
    $[81] = connector;
    $[82] = iconUrl;
    $[83] = resolvedIconColor;
    $[84] = t24;
    $[85] = t25;
    $[86] = t26;
    $[87] = t27;
    $[88] = t28;
    $[89] = t29;
  } else {
    t29 = $[89];
  }
  let t30;
  if ($[90] !== name) {
    t30 = <div className="body-sm-medium truncate">{name}</div>;
    $[90] = name;
    $[91] = t30;
  } else {
    t30 = $[91];
  }
  const t31 = !compact && subtitle ? subtitle : null;
  let t32;
  if ($[92] !== t30 || $[93] !== t31) {
    t32 = <div className="min-w-0 flex-1">{t30}{t31}</div>;
    $[92] = t30;
    $[93] = t31;
    $[94] = t32;
  } else {
    t32 = $[94];
  }
  let t33;
  if ($[95] !== t29 || $[96] !== t32) {
    t33 = <div className="flex items-center gap-3">{t29}{t32}</div>;
    $[95] = t29;
    $[96] = t32;
    $[97] = t33;
  } else {
    t33 = $[97];
  }
  let t34;
  if ($[98] !== bannerSlot || $[99] !== bodySlot || $[100] !== compact || $[101] !== executionDetailsSlot) {
    t34 = !compact && <>{bannerSlot}{bodySlot ? <div className="min-w-0 overflow-hidden">{bodySlot}</div> : null}{executionDetailsSlot}</>;
    $[98] = bannerSlot;
    $[99] = bodySlot;
    $[100] = compact;
    $[101] = executionDetailsSlot;
    $[102] = t34;
  } else {
    t34 = $[102];
  }
  let t35;
  if ($[103] !== hideSourceHandle || $[104] !== horizontalHandles) {
    t35 = !hideSourceHandle && <Handle type="source" position={horizontalHandles ? Position.Right : Position.Bottom} className="!border-ds-neutral-300 !bg-ds-surface-card !h-3 !w-3 !border-2 dark:!border-zinc-600" />;
    $[103] = hideSourceHandle;
    $[104] = horizontalHandles;
    $[105] = t35;
  } else {
    t35 = $[105];
  }
  let t36;
  if ($[106] !== commentsBadgeSlot || $[107] !== selectionIndicatorSlot || $[108] !== t22 || $[109] !== t23 || $[110] !== t33 || $[111] !== t34 || $[112] !== t35 || $[113] !== toolbarSlot) {
    t36 = <div className="bg-ds-surface-primary shadow-ds-float-deep flex flex-col gap-3 rounded-lg p-3">{toolbarSlot}{selectionIndicatorSlot}{t22}{t23}{t33}{t34}{commentsBadgeSlot}{t35}</div>;
    $[106] = commentsBadgeSlot;
    $[107] = selectionIndicatorSlot;
    $[108] = t22;
    $[109] = t23;
    $[110] = t33;
    $[111] = t34;
    $[112] = t35;
    $[113] = toolbarSlot;
    $[114] = t36;
  } else {
    t36 = $[114];
  }
  let t37;
  if ($[115] !== t15 || $[116] !== t16 || $[117] !== t17 || $[118] !== t18 || $[119] !== t19 || $[120] !== t20 || $[121] !== t21 || $[122] !== t36) {
    t37 = <div {...t15} ref={t16} data-api-node-id={t17} onMouseEnter={t18} onMouseLeave={t19} className={t20}>{t21}{t36}</div>;
    $[115] = t15;
    $[116] = t16;
    $[117] = t17;
    $[118] = t18;
    $[119] = t19;
    $[120] = t20;
    $[121] = t21;
    $[122] = t36;
    $[123] = t37;
  } else {
    t37 = $[123];
  }
  return t37;
}
export default ActionNodeShell;